import type { CampaignConfig } from "./types.js";
import {
  createCampaignLocalAssetSet,
  createCampaignVisualConfigFromManifest,
  defineCampaignVisualAssetFiles,
  withBase,
} from "./assets.js";
import { sanctifiedSkills } from "./skills.js";
import { sanctifiedAuthoredFields } from "./sanctified-fields.js";
import { createSharedGridMap } from "./shared-grid.js";

const visualAssets = defineCampaignVisualAssetFiles({
  mapBackground: "map-parchment.png",
  storyIntro: "story_intro_1.png",
  buildings: {
    Tower: "chapel.png",
    Wall: "wall.png",
    Amplifier: "amplifier.png",
    Trap: "trap.png",
    Shrine: "shrine.png",
  },
  pathEndpoints: {
    startStrip: "rift_anim_strip.png",
    startFallback: "rift.png",
    end: "altar.png",
  },
  monsters: {
    Normal: { sheet: "devil-iso-sheet.png", fallback: "devil-iso.png" },
    Swarm: { sheet: "imp-iso-sheet.png", fallback: "imp-iso.png" },
    Armored: { sheet: "fell-knight-iso-sheet.png", fallback: "fell-knight-iso.png" },
    Giant: { sheet: "daemon-iso-sheet.png", fallback: "daemon-iso.png" },
    Runner: { sheet: "devil-iso-sheet.png", fallback: "devil-iso.png" },
  },
});

const storyImageFiles = [
  visualAssets.storyIntro,
  "story_intro_2.png",
  "story_intro_3.png",
  "story_hollow_one_1.png",
] as const;

const visual = createCampaignVisualConfigFromManifest({
  campaignId: "sanctified",
  files: visualAssets,
  campaignLocalFiles: createCampaignLocalAssetSet(visualAssets, {
    extraFiles: storyImageFiles,
  }),
  mapDecorations: [
    { fileName: visualAssets.monsters.Normal.fallback, x: 10, y: 20, scale: 0.25 },
  ],
  storyImageFiles,
});

export const sanctifiedCampaign: CampaignConfig = {
  id: "sanctified",
  title: "Sanctified Lands",
  storageKey: "sanctified_save",
  tutorialStorageKey: "gc_tutorial_done",
  map: createSharedGridMap(sanctifiedAuthoredFields),
  story: {
    initialBeatId: "intro",
    fieldWinBeats: {
      G3: "hollow_one",
    },
    beats: {
      intro: [
        { imageSrc: visualAssets.storyIntro, textKey: "story.intro.panel1" },
        { imageSrc: "story_intro_2.png", textKey: "story.intro.panel2" },
        { imageSrc: "story_intro_3.png", textKey: "story.intro.panel3" },
      ],
      hollow_one: [
        { imageSrc: "story_hollow_one_1.png", textKey: "story.hollow_one.panel1" },
      ],
    },
  },
  visual,
  ui: {
    brandTitle: "Sanctified Lands",
    brandIcon: "✧",
    mageIcon: "☩",
    mageLabelKey: "hud.priest",
    resourceLabelKey: "hud.grace",
    waveTypeCodes: {
      Normal: "V",
      Swarm: "I",
      Armored: "F",
      Giant: "D",
      Runner: "R",
    },
  },
  audio: {
    tracks: {
      map: withBase("/assets/audio/maptheme.wav"),
      battle: withBase("/assets/audio/battletheme.wav"),
      win: withBase("/assets/audio/wintheme.wav"),
    },
  },
  skills: sanctifiedSkills,
};
